(function (root) {
  "use strict";

  /*
   * Pure-JavaScript click-track renderer: mono, 16-bit PCM WAV.
   *
   * Timing comes only from AudiocolourMetronomeCore (event iterator and
   * ticksToSamples) and sound only from AudiocolourMetronomeSounds (the same
   * click buffers live playback uses). Each event is placed at the frame
   * converted from its absolute tick position, so nothing accumulates.
   *
   * Rendering streams through fixed-size blocks in two passes: the first
   * measures the aggregate peak of the full mix (overlapping clicks summed),
   * the second applies the gain and writes PCM straight into the WAV buffer.
   * Peak memory is therefore the WAV itself plus one small block, which
   * matters on phones.
   *
   * Gain: if the mix peak exceeds OUTPUT_CEILING (-1 dBFS) the whole track is
   * attenuated to that ceiling; otherwise gain is exactly 1. The track is
   * never amplified.
   *
   * Limits: exports are capped at MAX_DURATION_SECONDS (10 minutes, about
   * 57.6 MB at 48 kHz) and checked before anything large is allocated.
   */
  var SAMPLE_RATES = Object.freeze([44100, 48000]);
  var CHANNELS = 1;
  var BITS_PER_SAMPLE = 16;
  var BYTES_PER_FRAME = CHANNELS * BITS_PER_SAMPLE / 8;
  var HEADER_BYTES = 44;
  var MAX_RIFF_CHUNK_SIZE = 0xFFFFFFFF;
  var MAX_DURATION_SECONDS = 600;
  var MAX_DATA_BYTES = MAX_DURATION_SECONDS * 48000 * BYTES_PER_FRAME;
  var OUTPUT_CEILING = Math.pow(10, -1 / 20);
  var PCM_SCALE = 32767;
  var BLOCK_FRAMES = 65536;
  var END_FADE_SECONDS = 0.005;
  var MAX_CLICK_SECONDS = 1;
  var LEVEL_CODES = Object.freeze(["accent", "normal", "soft", "subdivision"]);
  var MIME_TYPE = "audio/wav";

  function failure(code, message) {
    return { ok: false, error: { code: code, message: message } };
  }
  function success(value) { return Object.assign({ ok: true }, value); }
  function isInput(value) { return !!value && typeof value === "object" && !Array.isArray(value); }

  function dependencies() {
    var core = root.AudiocolourMetronomeCore;
    var sounds = root.AudiocolourMetronomeSounds;
    if (!core || !sounds) {
      return failure("missing_dependency", "Load audiocolour-metronome-core.js and audiocolour-metronome-sounds.js first.");
    }
    return success({ core: core, sounds: sounds });
  }

  /**
   * Checks that a WAV of this many frames fits the RIFF size field and the
   * export limit. Returns { ok, dataBytes, byteLength, riffChunkSize }.
   */
  function checkWavSize(frameCount) {
    if (!Number.isSafeInteger(frameCount) || frameCount < 1) {
      return failure("invalid_frame_count", "Frame count must be a safe whole number of at least 1.");
    }
    var dataBytes = frameCount * BYTES_PER_FRAME;
    var riffChunkSize = 36 + dataBytes;
    if (riffChunkSize > MAX_RIFF_CHUNK_SIZE) {
      return failure("riff_size_exceeded", "The audio is too large for a WAV file's 4 GB size field.");
    }
    if (dataBytes > MAX_DATA_BYTES) {
      return failure("export_too_large", "The click track would be larger than the 10-minute export limit.");
    }
    return success({ dataBytes: dataBytes, byteLength: HEADER_BYTES + dataBytes, riffChunkSize: riffChunkSize });
  }

  /**
   * Validates an export request and works out its size without rendering or
   * allocating audio. Options: settings (rhythm core settings), bars (whole
   * number, at least 1, after any count-in), sound, sampleRate (44100 or
   * 48000). Returns { ok, settings, bars, totalTicks, frameCount,
   * durationSeconds, musicalDurationSeconds, dataBytes, byteLength,
   * riffChunkSize, maxEvents }.
   */
  function estimateClickTrack(options) {
    var deps = dependencies();
    if (!deps.ok) return deps;
    var core = deps.core;
    if (!isInput(options)) return failure("invalid_input", "Options must be an object.");
    var soundCheck = deps.sounds.validateSound(options.sound);
    if (!soundCheck.ok) return soundCheck;
    if (SAMPLE_RATES.indexOf(options.sampleRate) === -1) {
      return failure("invalid_sample_rate", "Sample rate must be 44,100 or 48,000 Hz.");
    }
    var checked = core.validateSettings(options.settings);
    if (!checked.ok) return checked;
    var settings = checked.settings;
    var bars = options.bars;
    if (!Number.isSafeInteger(bars) || bars < 1) {
      return failure("invalid_bars", "Bars must be a whole number of at least 1.");
    }

    // Duration is checked in floating point first, so huge bar counts are
    // rejected before any exact tick or frame arithmetic.
    var barSeconds = core.barDuration({ bpm: settings.bpm, numerator: settings.numerator, denominator: settings.denominator }).seconds;
    var musicalDurationSeconds = (settings.countInBars + bars) * barSeconds;
    if (!(musicalDurationSeconds <= MAX_DURATION_SECONDS)) {
      return failure("export_too_long", "Click tracks can be at most 10 minutes long, including the count-in.");
    }
    var totalTicks = settings.countInTicks + bars * settings.barTicks;
    var frames = core.ticksToSamples(totalTicks, settings.bpm, options.sampleRate);
    if (!frames.ok) return frames;
    var size = checkWavSize(frames.samples);
    if (!size.ok) return size;
    return success({
      settings: settings,
      bars: bars,
      totalTicks: totalTicks,
      frameCount: frames.samples,
      durationSeconds: frames.samples / options.sampleRate,
      musicalDurationSeconds: core.ticksToSeconds(totalTicks, settings.bpm).seconds,
      dataBytes: size.dataBytes,
      byteLength: size.byteLength,
      riffChunkSize: size.riffChunkSize,
      maxEvents: (settings.countInBars + bars) * settings.numerator * settings.subdivision
    });
  }

  function invalidSoundSet(message) { return failure("invalid_sound_set", message); }
  function has(object, key) { return Object.prototype.hasOwnProperty.call(object, key); }

  /*
   * Checks a sound set in the shape generateSoundSet returns: { ok, sound,
   * sampleRate, clicks: { level: { ok, sound, level, sampleRate, samples,
   * length, durationSeconds, peak } } }. sound, sampleRate, clicks and every
   * level's samples are required; the other fields are checked when present.
   * Every sample must be finite, and a declared peak must equal the measured
   * peak, so a corrupted or mutated cached buffer is rejected rather than
   * rendered as silence or noise. Clicks may be at most MAX_CLICK_SECONDS
   * long, which bounds mixing work. Generated sets pass the same checks.
   */
  function validateSoundSet(soundSet, sound, sampleRate) {
    if (!isInput(soundSet)) return invalidSoundSet("soundSet must be an object from generateSoundSet.");
    if (has(soundSet, "ok") && soundSet.ok !== true) return invalidSoundSet("soundSet is a failed generateSoundSet result.");
    if (soundSet.sound !== sound) return invalidSoundSet("soundSet is for a different sound than the one requested.");
    if (soundSet.sampleRate !== sampleRate) return invalidSoundSet("soundSet is for a different sample rate than the one requested.");
    if (!isInput(soundSet.clicks)) return invalidSoundSet("soundSet must include clicks for every level.");
    var maxLength = Math.round(MAX_CLICK_SECONDS * sampleRate);
    for (var l = 0; l < LEVEL_CODES.length; l += 1) {
      var level = LEVEL_CODES[l];
      var click = soundSet.clicks[level];
      var where = "The " + level + " click ";
      if (!isInput(click)) return invalidSoundSet(where + "is missing.");
      if (has(click, "ok") && click.ok !== true) return invalidSoundSet(where + "is a failed generateClick result.");
      if (has(click, "level") && click.level !== level) return invalidSoundSet(where + "is labelled as a different level.");
      if (has(click, "sound") && click.sound !== sound) return invalidSoundSet(where + "is labelled as a different sound.");
      if (has(click, "sampleRate") && click.sampleRate !== sampleRate) return invalidSoundSet(where + "is labelled with a different sample rate.");
      var samples = click.samples;
      if (Object.prototype.toString.call(samples) !== "[object Float32Array]") return invalidSoundSet(where + "samples must be a Float32Array.");
      if (samples.length < 1 || samples.length > maxLength) {
        return invalidSoundSet(where + "must contain between 1 sample and " + MAX_CLICK_SECONDS + " second of samples.");
      }
      if (has(click, "length") && click.length !== samples.length) return invalidSoundSet(where + "declares a length that does not match its samples.");
      if (has(click, "durationSeconds") && click.durationSeconds !== samples.length / sampleRate) {
        return invalidSoundSet(where + "declares a duration that does not match its samples.");
      }
      var peak = 0;
      for (var i = 0; i < samples.length; i += 1) {
        var value = samples[i];
        if (!Number.isFinite(value)) return invalidSoundSet(where + "contains a non-finite sample at index " + i + ".");
        var magnitude = value < 0 ? -value : value;
        if (magnitude > peak) peak = magnitude;
      }
      if (has(click, "peak") && click.peak !== peak) return invalidSoundSet(where + "declares a peak that does not match its samples; the buffer may have been changed.");
    }
    return success({});
  }

  function writeAscii(view, offset, text) {
    for (var i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  }

  function writeHeader(view, sampleRate, dataBytes) {
    writeAscii(view, 0, "RIFF");
    view.setUint32(4, 36 + dataBytes, true);
    writeAscii(view, 8, "WAVE");
    writeAscii(view, 12, "fmt ");
    view.setUint32(16, 16, true);                                  // fmt chunk size
    view.setUint16(20, 1, true);                                   // PCM
    view.setUint16(22, CHANNELS, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * BYTES_PER_FRAME, true);        // byte rate
    view.setUint16(32, BYTES_PER_FRAME, true);                     // block align
    view.setUint16(34, BITS_PER_SAMPLE, true);
    writeAscii(view, 36, "data");
    view.setUint32(40, dataBytes, true);
  }

  // Sums every click overlapping [blockStart, blockStart + length) into block.
  function mixBlock(block, blockStart, length, events, clicks, cursor) {
    block.fill(0);
    var blockEnd = blockStart + length;
    while (cursor.first < events.count && events.frames[cursor.first] + events.maxLength <= blockStart) cursor.first += 1;
    for (var e = cursor.first; e < events.count; e += 1) {
      var frame = events.frames[e];
      if (frame >= blockEnd) break;
      var data = clicks[events.levels[e]];
      var from = frame > blockStart ? frame : blockStart;
      var to = frame + data.length < blockEnd ? frame + data.length : blockEnd;
      for (var x = from; x < to; x += 1) block[x - blockStart] += data[x - frame];
    }
  }

  /**
   * Renders a click track. Options are those of estimateClickTrack, plus an
   * optional soundSet (the result of generateSoundSet for the same sound and
   * sample rate) to reuse buffers already generated for live playback; the
   * buffers are only read, never changed. Returns { ok, bytes (Uint8Array of
   * the whole WAV file, ready for new Blob([bytes], { type: mimeType })),
   * mimeType, suggestedFilename, metadata }.
   */
  function renderClickTrack(options) {
    var estimate = estimateClickTrack(options);
    if (!estimate.ok) return estimate;
    var deps = dependencies();
    var core = deps.core;
    var settings = estimate.settings;
    var sampleRate = options.sampleRate;
    var frameCount = estimate.frameCount;

    // Validated before any event or audio buffers are allocated.
    var soundSet = options.soundSet;
    if (soundSet === undefined) {
      soundSet = deps.sounds.generateSoundSet(options.sound, sampleRate);
      if (!soundSet.ok) return soundSet;
    }
    var soundCheck = validateSoundSet(soundSet, options.sound, sampleRate);
    if (!soundCheck.ok) return soundCheck;
    var clicks = LEVEL_CODES.map(function (level) { return soundSet.clicks[level].samples; });
    var maxLength = Math.max.apply(null, clicks.map(function (data) { return data.length; }));

    // Event frames, bounded by the estimate's maximum event count. The raw
    // settings go to the iterator: normalised settings no longer carry accents.
    var created = core.createEventIterator(options.settings, { bars: estimate.bars });
    if (!created.ok) return created;
    var events = { frames: new Float64Array(estimate.maxEvents), levels: new Uint8Array(estimate.maxEvents), count: 0, maxLength: maxLength };
    var truncatedEvents = 0;
    var tailCut = false;
    for (var step = created.iterator.next(); !step.done; step = created.iterator.next()) {
      var event = step.value;
      var level = LEVEL_CODES.indexOf(event.accentLevel);
      if (level === -1) continue; // muted events are never requested, but never sounded either
      var frame = core.ticksToSamples(event.positionTicks, settings.bpm, sampleRate).samples;
      if (!Number.isSafeInteger(frame) || frame < 0) return failure("unsafe_sample_index", "An event fell outside the safe sample range.");
      if (frame >= frameCount) {
        truncatedEvents += 1;
        continue;
      }
      if (events.count >= estimate.maxEvents) return failure("too_many_events", "The rhythm produced more events than expected.");
      if (frame + clicks[level].length > frameCount) tailCut = true;
      events.frames[events.count] = frame;
      events.levels[events.count] = level;
      events.count += 1;
    }

    // Pass 1: aggregate peak of the complete mix.
    var block = new Float64Array(Math.min(BLOCK_FRAMES, frameCount));
    var cursor = { first: 0 };
    var mixPeak = 0;
    var start;
    var length;
    var i;
    for (start = 0; start < frameCount; start += block.length) {
      length = Math.min(block.length, frameCount - start);
      mixBlock(block, start, length, events, clicks, cursor);
      for (i = 0; i < length; i += 1) {
        var magnitude = block[i] < 0 ? -block[i] : block[i];
        if (magnitude > mixPeak) mixPeak = magnitude;
      }
    }
    var appliedGain = mixPeak > OUTPUT_CEILING ? OUTPUT_CEILING / mixPeak : 1;

    // Pass 2: gain, optional end fade, 16-bit little-endian PCM.
    var buffer = new ArrayBuffer(estimate.byteLength);
    var view = new DataView(buffer);
    writeHeader(view, sampleRate, estimate.dataBytes);
    var fadeFrames = tailCut ? Math.min(frameCount, Math.round(END_FADE_SECONDS * sampleRate)) : 0;
    var fadeStart = frameCount - fadeFrames;
    var outputPeak = 0;
    var clippedSamples = 0;
    cursor.first = 0;
    for (start = 0; start < frameCount; start += block.length) {
      length = Math.min(block.length, frameCount - start);
      mixBlock(block, start, length, events, clicks, cursor);
      for (i = 0; i < length; i += 1) {
        var index = start + i;
        var value = block[i] * appliedGain;
        if (index >= fadeStart) value *= 0.5 * (1 + Math.cos(Math.PI * (index - fadeStart + 1) / fadeFrames));
        // Halves round away from zero, so positive and negative peaks match.
        var scaled = value * PCM_SCALE;
        var level16 = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
        if (level16 > 32767) { level16 = 32767; clippedSamples += 1; }
        if (level16 < -32768) { level16 = -32768; clippedSamples += 1; }
        var absolute = value < 0 ? -value : value;
        if (absolute > outputPeak) outputPeak = absolute;
        view.setInt16(HEADER_BYTES + index * BYTES_PER_FRAME, level16 === 0 ? 0 : level16, true);
      }
    }

    var bpmLabel = String(settings.bpm).replace(".", "-");
    return success({
      bytes: new Uint8Array(buffer),
      mimeType: MIME_TYPE,
      suggestedFilename: "audiocolour-click-" + bpmLabel + "bpm-" + settings.numerator + "-" + settings.denominator +
        "-" + options.sound + "-" + (sampleRate / 1000).toString().replace(".", "-") + "k.wav",
      metadata: Object.freeze({
        sampleRate: sampleRate,
        channels: CHANNELS,
        bitsPerSample: BITS_PER_SAMPLE,
        frameCount: frameCount,
        durationSeconds: estimate.durationSeconds,
        musicalDurationSeconds: estimate.musicalDurationSeconds,
        byteLength: estimate.byteLength,
        dataBytes: estimate.dataBytes,
        bpm: settings.bpm,
        numerator: settings.numerator,
        denominator: settings.denominator,
        bars: estimate.bars,
        countInBars: settings.countInBars,
        sound: options.sound,
        eventCount: events.count,
        truncatedEvents: truncatedEvents,
        endFadeApplied: fadeFrames > 0,
        mixPeak: mixPeak,
        appliedGain: appliedGain,
        outputPeak: outputPeak,
        outputCeiling: OUTPUT_CEILING,
        clippedSamples: clippedSamples
      })
    });
  }

  root.AudiocolourMetronomeWav = Object.freeze({
    sampleRates: SAMPLE_RATES,
    channels: CHANNELS,
    bitsPerSample: BITS_PER_SAMPLE,
    headerBytes: HEADER_BYTES,
    mimeType: MIME_TYPE,
    outputCeiling: OUTPUT_CEILING,
    limits: Object.freeze({
      maxDurationSeconds: MAX_DURATION_SECONDS,
      maxDataBytes: MAX_DATA_BYTES,
      maxRiffChunkSize: MAX_RIFF_CHUNK_SIZE,
      maxClickSeconds: MAX_CLICK_SECONDS,
      blockFrames: BLOCK_FRAMES
    }),
    checkWavSize: checkWavSize,
    estimateClickTrack: estimateClickTrack,
    renderClickTrack: renderClickTrack
  });
}(globalThis));
